import { useAuth0 } from "@auth0/auth0-react";
import { Link } from "react-router-dom";

import useBrandList from "hooks/useBrandList";
import { buildBrandPath, buildClassificationPath } from "utils/productRoutes";

import Logo from "./Logo";
import { PROMOTION_NAV_ITEMS } from "./navigationItems";

import { Icon, IconButton } from "@/design-system";

const primaryLinkClass =
  "flex min-h-[42px] items-center gap-[var(--space-3)] rounded-md px-[var(--space-3)] py-[9px] font-sans text-sm font-semibold text-ink [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

const childLinkClass =
  "block rounded-sm px-[var(--space-3)] py-[8px] font-sans text-sm text-ink-2 [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-soft hover:text-blue-700 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

function ExpandButton({ label, expanded, onClick }) {
  return (
    <button
      type="button"
      aria-label={`${expanded ? "Collapse" : "Expand"} ${label}`}
      aria-expanded={expanded}
      className="inline-flex h-[38px] w-[38px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-ink-3 hover:bg-neutral-100 hover:text-ink focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
      onClick={onClick}
    >
      <Icon
        name="expand_more"
        size={20}
        weight={400}
        className={`[transition:transform_var(--duration-base)_var(--ease-spring)] ${
          expanded ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

/**
 * A top-level drawer row where the **whole row** toggles its section open.
 *
 * The other sections are a `<Link>` plus a 38px `ExpandButton`, so on a full-width row the
 * only thing that expands is a thumb-sized corner and everything else navigates away. That
 * is wrong for the two sections whose entire point is the list they hold — categories and
 * brands — so those use this instead.
 *
 * Where the row also has a destination worth keeping, put it back as the first child link
 * rather than making the row navigate. "All categories" does that with "All products".
 */
function SectionToggle({ icon, label, expanded, onClick, className = "" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-expanded={expanded}
      className={`${primaryLinkClass} w-full cursor-pointer justify-between border-0 bg-transparent text-left ${className}`}
    >
      <span className="flex items-center gap-[var(--space-3)]">
        <Icon name={icon} size={20} weight={400} />
        {label}
      </span>
      <Icon
        name="expand_more"
        size={20}
        weight={400}
        className={`shrink-0 text-ink-3 [transition:transform_var(--duration-base)_var(--ease-spring)] ${
          expanded ? "rotate-180" : ""
        }`}
      />
    </button>
  );
}

export default function MobileDrawer({
  isOpen,
  onClose,
  classification = [],
  expandedMenus,
  toggleMenu,
}) {
  const { isAuthenticated, loginWithRedirect, logout, user } = useAuth0();
  const isExpanded = (key) => Boolean(expandedMenus[key]);
  const {
    brands,
    loading: brandsLoading,
    error: brandsError,
    loadBrands,
  } = useBrandList();

  // Fetch on the first expand, not on mount — the drawer renders on every page below `lg`.
  const toggleBrands = () => {
    if (!isExpanded("Brands")) loadBrands();
    toggleMenu("Brands");
  };

  return (
    <aside
      aria-label="Mobile navigation"
      aria-hidden={!isOpen}
      className={`fixed left-0 top-0 z-[var(--z-drawer)] flex h-full w-[min(360px,90vw)] flex-col bg-card shadow-xl [transition:transform_var(--duration-base)_var(--ease-out),visibility_var(--duration-base)] ${
        isOpen
          ? "visible translate-x-0"
          : "invisible -translate-x-full pointer-events-none"
      }`}
    >
      <div className="flex items-center justify-between border-b border-line-subtle px-[var(--space-5)] py-[var(--space-4)]">
        <Logo />
        <IconButton
          icon={<Icon name="close" size={22} weight={500} />}
          variant="ghost"
          size="sm"
          aria-label="Close navigation menu"
          onClick={onClose}
        />
      </div>

      <nav className="dnd-thinscroll flex-1 overflow-y-auto px-[var(--space-3)] py-[var(--space-3)]">
        <Link to="/" onClick={onClose} className={primaryLinkClass}>
          <Icon name="home" size={20} weight={400} />
          Home
        </Link>

        <div>
          <SectionToggle
            icon="grid_view"
            label="All categories"
            expanded={isExpanded("Products")}
            onClick={() => toggleMenu("Products")}
            className="text-blue-700"
          />

          {isExpanded("Products") && (
            <div className="ml-[var(--space-4)] border-l border-line-subtle pl-[var(--space-2)]">
              {/*
                The row used to be a `<Link to="/products">`, so this was reachable by
                tapping the label. Now that the row expands instead, `/products` would have
                no entry point in the drawer at all — this is it.
              */}
              <Link
                to="/products"
                onClick={onClose}
                className={`${childLinkClass} font-semibold text-blue-700`}
              >
                All products
              </Link>

              {classification.map((category) => (
                <div key={`${category.id}-${category.name}`}>
                  <div className="flex items-center">
                    <Link
                      to={buildClassificationPath(category)}
                      onClick={onClose}
                      className={`${childLinkClass} min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap`}
                    >
                      {category.name}
                    </Link>
                    {category.subcategories.length > 0 && (
                      <ExpandButton
                        label={category.name}
                        expanded={isExpanded(`category-${category.id}`)}
                        onClick={() => toggleMenu(`category-${category.id}`)}
                      />
                    )}
                  </div>
                  {isExpanded(`category-${category.id}`) && (
                    <div className="ml-[var(--space-3)] border-l border-line-subtle pl-[var(--space-2)]">
                      {category.subcategories.map((subcategory) => (
                        <Link
                          key={`${subcategory.id}-${subcategory.name}`}
                          to={buildClassificationPath(subcategory)}
                          className={childLinkClass}
                          onClick={onClose}
                        >
                          {subcategory.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/*
          Brands. The desktop nav has this as a hover panel in `BrandsDropdown`, which is
          `hidden lg:block` and so absent here entirely — this is the only way to reach a
          brand below `lg`.

          "All brands" heads the expanded list, mirroring "All categories" → "All
          products": the row itself toggles, so the destination lives inside the list.
          `buildBrandPath` is what makes the awkward names work — 6 of the 313 contain
          `&` or `/`.
        */}
        <div>
          <SectionToggle
            icon="storefront"
            label="Brands"
            expanded={isExpanded("Brands")}
            onClick={toggleBrands}
          />

          {isExpanded("Brands") && (
            <div className="ml-[var(--space-4)] border-l border-line-subtle pl-[var(--space-2)]">
              <Link
                to="/brands"
                onClick={onClose}
                className={`${childLinkClass} font-semibold text-blue-700`}
              >
                All brands
              </Link>

              {brandsError ? (
                <p className="px-[var(--space-3)] py-[8px] text-2xs text-ink-3">
                  Couldn&apos;t load brands.
                </p>
              ) : brandsLoading && !brands.length ? (
                <p className="px-[var(--space-3)] py-[8px] text-2xs text-ink-3">
                  Loading brands…
                </p>
              ) : (
                brands.map((brand) => (
                  <Link
                    key={`${brand.brand_id}-${brand.brand_name}`}
                    to={buildBrandPath(brand.brand_name)}
                    onClick={onClose}
                    className={childLinkClass}
                  >
                    {brand.brand_name}
                  </Link>
                ))
              )}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center">
            <Link
              to="/promotions"
              onClick={onClose}
              className={`${primaryLinkClass} min-w-0 flex-1 text-orange-600`}
            >
              <Icon name="sell" size={20} weight={400} fill />
              Promotions & Catalogues
            </Link>
            <ExpandButton
              label="Promotions & Catalogues"
              expanded={isExpanded("Promotions")}
              onClick={() => toggleMenu("Promotions")}
            />
          </div>
          {isExpanded("Promotions") && (
            <div className="ml-[var(--space-4)] border-l border-line-subtle pl-[var(--space-2)]">
              {PROMOTION_NAV_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`${childLinkClass} flex items-center gap-[var(--space-2)]`}
                  onClick={onClose}
                >
                  <Icon name={item.icon} size={18} weight={400} />
                  {item.label}
                </Link>
              ))}
            </div>
          )}
        </div>

        <Link to="/partners" onClick={onClose} className={primaryLinkClass}>
          <Icon name="handshake" size={20} weight={400} />
          Partners
        </Link>

        {isAuthenticated && (
          <>
            <Link
              to="/favorites"
              onClick={onClose}
              className={primaryLinkClass}
            >
              <Icon name="favorite" size={20} weight={400} />
              Saved lists
            </Link>
            <Link to="/re-order" onClick={onClose} className={primaryLinkClass}>
              <Icon name="history" size={20} weight={400} />
              Order again
            </Link>
          </>
        )}

        {/* The old "About Us" group moved to the footer's "We're here" column on
            request — the drawer mirrors the desktop nav, which no longer carries it. */}
      </nav>

      <div className="border-t border-line-subtle px-[var(--space-5)] py-[var(--space-4)]">
        {!isAuthenticated ? (
          <button
            type="button"
            className="inline-flex h-[42px] w-full cursor-pointer items-center justify-center gap-[var(--space-2)] rounded-pill border-[1.5px] border-line bg-card px-[18px] font-sans text-sm font-semibold text-blue-700 hover:border-blue-400 hover:bg-soft focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
            onClick={() => {
              loginWithRedirect({ screen_hint: "login" });
              onClose();
            }}
          >
            <Icon name="person" size={20} weight={400} />
            Sign in
          </button>
        ) : (
          <div className="flex items-center gap-[var(--space-3)]">
            <span className="min-w-0 flex-1">
              <span className="block text-xs text-ink-3">Signed in as</span>
              <Link
                to="/profile"
                onClick={onClose}
                className="block overflow-hidden text-ellipsis whitespace-nowrap text-sm font-semibold text-ink hover:text-blue-700"
              >
                {user?.name || user?.email || "User"}
              </Link>
            </span>
            <IconButton
              icon={<Icon name="logout" size={20} weight={400} />}
              variant="ghost"
              size="sm"
              aria-label="Sign out"
              onClick={() => {
                logout({ returnTo: window.location.origin });
                onClose();
              }}
            />
          </div>
        )}
      </div>
    </aside>
  );
}
